import {
  getGetJobByIdQueryKey,
  getListHealthConcernsQueryKey,
} from "@elation/api-client/src/stitch-web";
import { Button } from "@src/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/components/ui/popover";
import { useCurrentPatient } from "@src/stores/app";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useCallback } from "react";
import { ImportPanel } from "./ImportPanel";
import { useDocumentJobs } from "./useDocumentJobs";

interface DocumentUploadProps {
  onNewOpportunities: (ids: number[]) => void;
}

interface CompletedJobMetadata {
  opportunityIds?: number[];
}

const DocumentUploadInner = ({
  patientId,
  onNewOpportunities,
}: {
  patientId: number;
  onNewOpportunities: (ids: number[]) => void;
}) => {
  const queryClient = useQueryClient();
  const { jobs, addJob, markJobFailed, removeJob } = useDocumentJobs(patientId);

  const handleJobComplete = useCallback(
    (jobId: string) => {
      queryClient.invalidateQueries({
        queryKey: getListHealthConcernsQueryKey(patientId),
      });

      const jobData = queryClient.getQueryData(getGetJobByIdQueryKey(jobId)) as
        | { data?: { metadata?: CompletedJobMetadata } }
        | undefined;
      const newIds = jobData?.data?.metadata?.opportunityIds ?? [];
      if (newIds.length > 0) {
        onNewOpportunities(newIds);
      }
    },
    [patientId, queryClient, onNewOpportunities],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus />
          Import Document
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px]">
        <ImportPanel
          patientId={patientId}
          jobs={jobs}
          onJobAdded={addJob}
          onJobFailed={markJobFailed}
          onJobComplete={handleJobComplete}
          onJobRemove={removeJob}
        />
      </PopoverContent>
    </Popover>
  );
};

export const DocumentUpload = ({ onNewOpportunities }: DocumentUploadProps) => {
  const currentPatient = useCurrentPatient();

  if (!currentPatient?.id) return null;

  return (
    <DocumentUploadInner
      patientId={currentPatient.id}
      onNewOpportunities={onNewOpportunities}
    />
  );
};
